import { useParams } from "@solidjs/router";

import CreditLine from "@repo/kikoff-models/lending/CreditLine";
import Subscription from "@repo/kikoff-models/Subscription";
import Transaction from "@repo/kikoff-models/Transaction";
import Telemetry from "@repo/telemetry";
import Analytics from "@repo/utils/Analytics";
import UArray from "@repo/utils/UArray";

import appSettings from "~/appSettings";
import { useCreditLines } from "~/state/features/creditLines";
import { useSubscriptions } from "~/state/features/subscriptions";
import { useTransactions } from "~/state/features/transactions";
import { useUser } from "~/state/features/user";

namespace Marketing {
  export function useTier(properties: Analytics.Properties): Subscription.Tier {
    if (UArray.includesLiteral(Subscription.tiers, properties.tier))
      return properties.tier;

    const params = useParams<{
      tier?: Subscription.Tier;
    }>();

    if (params.tier)
      if (Subscription.tiers.includes(params.tier)) return params.tier;
      else
        Telemetry.captureError(
          new Error(`Invalid tier url param: ${params.tier}`)
        );

    const [subscriptions] = useSubscriptions();
    const activeTier = subscriptions.active()?.plan.tier;
    if (activeTier) return activeTier;

    Telemetry.captureError(
      new Error(
        "Unable to find subscription from context, defaulting to basic."
      )
    );
    return "basic";
  }

  export function useExpectedLtv(properties: Analytics.Properties) {
    const [user] = useUser();

    const tier = useTier(properties);

    return user.expectedLtv() || appSettings.marketing.ltvByTier[tier];
  }

  export function useTransactionPrice(properties: Analytics.Properties) {
    const [transactions] = useTransactions();
    const [creditLines] = useCreditLines();

    const transaction = transactions.byId(
      properties.transactionId as Transaction.Id
    );
    if (transaction) return transaction.amountCents;

    const creditLineAccount = creditLines.byId(
      properties.accountId as CreditLine.Id
    );
    if (creditLineAccount)
      return creditLineAccount.agreement.minimumPaymentAmountCents;

    Telemetry.captureError(
      new Error(
        "Unable to compute transaction amount from context, defaulting to open credit line minimum payment if present."
      )
    );

    return creditLines.activeCa()?.agreement.minimumPaymentAmountCents;
  }
}

export default Marketing;
